import SiteInertiaController from '@/actions/App/Http/Controllers/SiteInertiaController';
import { parseDateFromUrl } from '@/lib/date';
import { type SearchFormData, type Site } from '@/types/search';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm as useInertiaForm } from '@inertiajs/react';
import { parseISO } from 'date-fns';
import { createContext, useCallback, useContext, useMemo, useReducer, useState } from 'react';
import { useForm, useWatch, type UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { useSearchHistory, type HistoryItem } from './use-search-history';
import { useSiteIndex, type SiteIndex } from './use-site-index';

/**
 * `date` is optional on purpose.
 *
 * The previous default of `formatDateForUrl(new Date())` let the *server's* clock
 * and timezone decide the rendered string, so a UTC server and an Asia/Ho_Chi_Minh
 * client disagreed after 17:00 local — a hydration mismatch on the home hero every
 * evening. It also silently overrode the server's own rule: SiteInertiaController
 * ::search() already redirects a missing date to `hour >= 16 ? tomorrow : today`.
 *
 * Omitting an empty date on submit hands that decision back to the controller. The
 * date panel still pre-selects today, but it does so client-side after a gesture.
 */
const searchSchema = z.object({
  site: z.string().min(1, 'Vui lòng chọn điểm đến'),
  // A union rather than .optional(): this keeps the inferred type `string`, which
  // matches SearchFormData exactly. '' is the "not chosen" value and is stripped
  // on submit, so it never reaches the URL.
  date: z.union([z.literal(''), z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ngày không hợp lệ')]),
});

export type ZoneId = 'destination' | 'date';

export type SheetStep = 'destination' | 'date';

/** Which surface is open, and where within it. One value, so the two can't disagree. */
type Surface = { kind: 'closed' } | { kind: 'sheet'; step: SheetStep } | { kind: 'popover'; zone: ZoneId };

type SurfaceAction =
  | { type: 'OPEN_SHEET'; step: SheetStep }
  | { type: 'OPEN_ZONE'; zone: ZoneId }
  | { type: 'CLOSE' }
  | { type: 'BACK' }
  | { type: 'SITE_PICKED' }
  | { type: 'DATE_PICKED' };

function surfaceReducer(state: Surface, action: SurfaceAction): Surface {
  switch (action.type) {
    case 'OPEN_SHEET':
      return { kind: 'sheet', step: action.step };
    case 'OPEN_ZONE':
      return { kind: 'popover', zone: action.zone };
    case 'CLOSE':
      return { kind: 'closed' };

    case 'BACK':
      if (state.kind === 'sheet') return { kind: 'sheet', step: 'destination' };
      if (state.kind === 'popover') return { kind: 'popover', zone: 'destination' };
      return state;

    // Both surfaces advance to the date step. Stacking destinations and dates on
    // one scrolling column pushed the date rail below the fold once the
    // destination list filled out, so the sheet steps through them too.
    case 'SITE_PICKED':
      if (state.kind === 'sheet') return { kind: 'sheet', step: 'date' };
      if (state.kind === 'popover') return { kind: 'popover', zone: 'date' };
      return state;

    // Desktop dismisses once a date is chosen. On mobile the sticky CTA is the only
    // exit, so closing here would strand the user with no way to submit.
    case 'DATE_PICKED':
      return state.kind === 'popover' ? { kind: 'closed' } : state;

    default:
      return state;
  }
}

export interface SearchBarState {
  form: UseFormReturn<SearchFormData>;
  siteValue: string;
  dateValue: string;
  selectedSite: Site | undefined;
  selectedDate: Date | undefined;

  isSheetOpen: boolean;
  sheetStep: SheetStep;
  openZone: ZoneId | null;
  openSheet: () => void;
  openZoneAt: (zone: ZoneId) => void;
  backToDestination: () => void;
  close: () => void;

  pickSite: (site: Site) => void;
  pickDate: (date: Date) => void;
  submit: () => void;

  sites: Site[];
  index: SiteIndex;
  history: HistoryItem[];
  clearHistory: () => void;

  isSubmitting: boolean;
  submitError: string | null;
  announcement: string;
}

export function useSearchBar({ sites, initialData }: { sites: Site[]; initialData?: Partial<SearchFormData> }): SearchBarState {
  const form = useForm<SearchFormData>({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      site: initialData?.site ?? '',
      date: initialData?.date ? parseDateFromUrl(initialData.date) : '',
    },
  });

  // useWatch rather than form.watch: both fields only change on a click, so the
  // consumer re-renders about twice per session instead of on every form event.
  const [siteValue, dateValue] = useWatch({ control: form.control, name: ['site', 'date'] }) as [string, string];

  const [surface, dispatch] = useReducer(surfaceReducer, { kind: 'closed' } as Surface);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const index = useSiteIndex(sites);
  const { history, addHistory, clearHistory } = useSearchHistory();
  const { get: inertiaGet, processing } = useInertiaForm();

  const selectedSite = index.byMappingCode(siteValue);
  const selectedDate = useMemo(() => (dateValue ? parseISO(dateValue) : undefined), [dateValue]);

  const pickSite = useCallback(
    (site: Site) => {
      form.setValue('site', site.mapping_code ?? '', { shouldValidate: true, shouldDirty: true });
      addHistory({ code: site.mapping_code ?? '', name: site.name });
      setSubmitError(null);
      setAnnouncement(`Đã chọn ${site.name}. Tiếp theo, chọn ngày đi.`);
      dispatch({ type: 'SITE_PICKED' });
    },
    [form, addHistory],
  );

  const backToDestination = useCallback(() => {
    setAnnouncement('Quay lại chọn điểm đến.');
    dispatch({ type: 'BACK' });
  }, []);

  const pickDate = useCallback(
    (date: Date) => {
      // Local parts, not toISOString() — the latter converts to UTC and lands on
      // the previous day for any timezone east of Greenwich, which is all of Vietnam.
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      form.setValue('date', value, { shouldValidate: true, shouldDirty: true });
      dispatch({ type: 'DATE_PICKED' });
    },
    [form],
  );

  const submit = useCallback(() => {
    void form.handleSubmit((data) => {
      const query: Record<string, unknown> = { site: data.site };
      // Absent rather than empty, so the controller's own 16:00 rule applies.
      if (data.date) query.date = data.date;

      setSubmitError(null);
      inertiaGet(SiteInertiaController.search({ query }).url, {
        onError: (errors) => {
          setSubmitError(errors.site ?? errors.date ?? 'Không thể tìm kiếm. Vui lòng thử lại.');
        },
      });
    })();
  }, [form, inertiaGet]);

  return {
    form,
    siteValue,
    dateValue,
    selectedSite,
    selectedDate,

    isSheetOpen: surface.kind === 'sheet',
    sheetStep: surface.kind === 'sheet' ? surface.step : 'destination',
    openZone: surface.kind === 'popover' ? surface.zone : null,
    // Opening with a destination already chosen (arriving from /dich-vu?site=…)
    // lands straight on the date step — re-picking the same destination to reach
    // the calendar is a step that buys the user nothing. The header's back
    // affordance is there when they do want to change it.
    openSheet: useCallback(() => dispatch({ type: 'OPEN_SHEET', step: siteValue ? 'date' : 'destination' }), [siteValue]),
    openZoneAt: useCallback((zone: ZoneId) => dispatch({ type: 'OPEN_ZONE', zone }), []),
    backToDestination,
    close: useCallback(() => dispatch({ type: 'CLOSE' }), []),

    pickSite,
    pickDate,
    submit,

    sites,
    index,
    history,
    clearHistory,

    isSubmitting: processing,
    submitError,
    announcement,
  };
}

const SearchBarContext = createContext<SearchBarState | null>(null);

export const SearchBarProvider = SearchBarContext.Provider;

export function useSearchBarContext(): SearchBarState {
  const value = useContext(SearchBarContext);
  if (!value) throw new Error('useSearchBarContext must be used inside <SearchBarProvider>');
  return value;
}
